import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Edit, Trash2, LogOut, Save, X, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = ["Archivo", "Glosario", "Antipoesía", "Currículum", "Mensajes"];

const EMPTY = {
    title: "",
    excerpt: "",
    content: "",
    category: "Archivo",
    cover_image: "",
    published: true,
};

export default function Admin() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null); // null = none, "new" = new, post = existing
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);

    async function reload() {
        try {
            const { data } = await api.get("/admin/blog");
            setPosts(data);
        } catch (e) {
            toast.error("No se pudo cargar el archivo.");
        }
    }

    useEffect(() => {
        reload();
    }, []);

    function startNew() {
        setForm(EMPTY);
        setEditing("new");
    }

    function startEdit(post) {
        setForm({
            title: post.title,
            excerpt: post.excerpt,
            content: post.content,
            category: post.category,
            cover_image: post.cover_image || "",
            published: post.published,
        });
        setEditing(post);
    }

    function cancel() {
        setEditing(null);
        setForm(EMPTY);
    }

    async function save(e) {
        e.preventDefault();
        setLoading(true);
        const payload = { ...form };
        if (!payload.cover_image) delete payload.cover_image;
        try {
            if (editing === "new") {
                await api.post("/admin/blog", payload);
                toast.success("Entrada creada.");
            } else {
                await api.put(`/admin/blog/${editing.id}`, payload);
                toast.success("Entrada actualizada.");
            }
            await reload();
            cancel();
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "Error al guardar.",
            );
        } finally {
            setLoading(false);
        }
    }

    async function remove(post) {
        if (!confirm(`¿Eliminar "${post.title}"? No hay vuelta atrás.`)) return;
        try {
            await api.delete(`/admin/blog/${post.id}`);
            toast.success("Entrada eliminada.");
            await reload();
        } catch (e) {
            toast.error("Error al eliminar.");
        }
    }

    async function togglePublished(post) {
        try {
            await api.put(`/admin/blog/${post.id}`, {
                published: !post.published,
            });
            toast.success(
                post.published ? "Entrada despublicada." : "Entrada publicada.",
            );
            await reload();
        } catch (e) {
            toast.error("Error al actualizar.");
        }
    }

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    return (
        <>
            <SEO title="Admin · Blog" description="Panel administrativo del blog" path="/admin/blog" />
            <section className="py-12 md:py-16" data-testid="admin-panel">
                <div className="container-x">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-12 pb-8 border-b border-[var(--line)]">
                        <div>
                            <Link
                                to="/admin"
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-4"
                                data-testid="admin-back-to-hub"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Volver al panel
                            </Link>
                            <p className="eyebrow mb-3">Departamento · Blog</p>
                            <h1 className="font-display text-4xl md:text-6xl text-[var(--paper-2)]">
                                Archivo · Administración
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                Sesión: {user?.email} · Rol: {user?.role}
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={startNew}
                                className="btn-brutal btn-brutal-danger"
                                data-testid="admin-new-post"
                            >
                                <Plus className="w-4 h-4" />
                                Nueva entrada
                            </button>
                            <button
                                onClick={handleLogout}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="admin-logout"
                            >
                                <LogOut className="w-4 h-4" />
                                Salir
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    {editing && (
                        <form
                            onSubmit={save}
                            className="dossier mb-12"
                            data-testid="admin-form"
                        >
                            <div className="dossier-header">
                                <span>
                                    {editing === "new"
                                        ? "Nueva entrada"
                                        : `Editando: ${editing.title}`}
                                </span>
                                <button
                                    type="button"
                                    onClick={cancel}
                                    className="text-[var(--ink-dim)] hover:text-[var(--danger)]"
                                    data-testid="admin-form-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="dossier-content space-y-6">
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                        Título
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={form.title}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                title: e.target.value,
                                            })
                                        }
                                        className="input-dossier"
                                        data-testid="admin-form-title"
                                    />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                            Categoría
                                        </label>
                                        <select
                                            value={form.category}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    category: e.target.value,
                                                })
                                            }
                                            className="input-dossier"
                                            data-testid="admin-form-category"
                                        >
                                            {CATEGORIES.map((c) => (
                                                <option key={c} value={c} style={{ background: "#111" }}>
                                                    {c}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                            URL Imagen portada (opcional)
                                        </label>
                                        <input
                                            type="url"
                                            value={form.cover_image}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    cover_image: e.target.value,
                                                })
                                            }
                                            className="input-dossier"
                                            placeholder="https://…"
                                            data-testid="admin-form-cover"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                        Resumen (excerpt)
                                    </label>
                                    <textarea
                                        required
                                        rows={2}
                                        value={form.excerpt}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                excerpt: e.target.value,
                                            })
                                        }
                                        className="input-dossier resize-none"
                                        data-testid="admin-form-excerpt"
                                    />
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                        Contenido (markdown soportado: ## h2, ### h3, &gt; blockquote, **bold**, [link](url), - lista)
                                    </label>
                                    <textarea
                                        required
                                        rows={16}
                                        value={form.content}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                content: e.target.value,
                                            })
                                        }
                                        className="input-dossier resize-y"
                                        data-testid="admin-form-content"
                                    />
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="published"
                                        checked={form.published}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                published: e.target.checked,
                                            })
                                        }
                                        className="w-4 h-4 accent-[var(--danger)]"
                                        data-testid="admin-form-published"
                                    />
                                    <label
                                        htmlFor="published"
                                        className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--paper-2)] cursor-pointer"
                                    >
                                        Publicada
                                    </label>
                                </div>
                                <div className="flex gap-3 pt-4 border-t border-dashed border-[var(--ink-dim)]">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-brutal btn-brutal-danger disabled:opacity-60"
                                        data-testid="admin-form-save"
                                    >
                                        <Save className="w-4 h-4" />
                                        {loading ? "Guardando…" : "Guardar"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={cancel}
                                        className="btn-brutal btn-brutal-ghost"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Posts list */}
                    <div className="space-y-3" data-testid="admin-posts-list">
                        <p className="eyebrow mb-4">
                            Entradas · {posts.length} total
                        </p>
                        {posts.map((post) => (
                            <article
                                key={post.id}
                                className="border border-[var(--line)] bg-[#1a1a1a] p-5 flex flex-wrap items-center gap-4 hover:border-[var(--danger)] transition-colors"
                                data-testid={`admin-post-${post.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-1">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)]">
                                            {post.category}
                                        </span>
                                        {!post.published && (
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--warning)]">
                                                Borrador
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-display text-xl text-[var(--paper-2)] truncate">
                                        {post.title}
                                    </h3>
                                    <p className="font-mono text-xs text-[var(--ink-muted)] truncate">
                                        /blog/{post.slug}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => togglePublished(post)}
                                        className="p-2 border border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--warning)] hover:text-[var(--warning)]"
                                        title={post.published ? "Despublicar" : "Publicar"}
                                        data-testid={`admin-toggle-${post.id}`}
                                    >
                                        {post.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={() => startEdit(post)}
                                        className="p-2 border border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--danger)] hover:text-[var(--danger)]"
                                        data-testid={`admin-edit-${post.id}`}
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => remove(post)}
                                        className="p-2 border border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)]"
                                        data-testid={`admin-delete-${post.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </article>
                        ))}
                        {posts.length === 0 && (
                            <p className="font-mono text-sm text-[var(--ink-muted)] py-10 text-center">
                                Sin entradas. Crea la primera.
                            </p>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
